import { useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { Effect, FiberId } from 'effect';
import type { RuntimeFiber } from 'effect/Fiber';
import type { LookGroupDTOType } from '../../../../schemas/styling';
import {
  useLazyGetLookByIdQuery,
  usePublishLookMutation,
  useReRunLookMutation,
  useRejectLookMutation,
  useUnpublishLookMutation,
} from '../../../../store/stylist/stylistApi';

import { getBackoffDelayMs } from './helpers';
import { WorkflowRunItemProps } from './types';
import { formatWorkflowLine } from '../../../LookGroups/helpers';
import { toGroupLookSummary } from '../../../LookGroups/components/LookGroupWorkspace/utils';
import LookGroupWorkspaceRow from '../../../LookGroups/components/LookGroupWorkspace/LookGroupWorkspaceRow';
import { WorkflowRunItemHeader } from './components/WorkflowRunItemHeader';

const WorkflowRunItem = ({
  run,
  canApprove = false,
  canReject = false,
  canEdit = true,
  lastItemRef,
}: WorkflowRunItemProps) => {
  const [isPollingLook, setIsPollingLook] = useState(false);
  const [latestLookData, setLatestLookData] = useState<typeof run | null>(null);

  const [rejectLook, { isLoading: isRejecting }] = useRejectLookMutation();
  const [publishLook, { isLoading: isPublishing }] = usePublishLookMutation();
  const [unpublishLook, { isLoading: isUnpublishing }] = useUnpublishLookMutation();
  const [reRunLook, { isLoading: isReRunning }] = useReRunLookMutation();
  const [triggerGetLookById] = useLazyGetLookByIdQuery();

  const currentLook = latestLookData || run;
  const workflowGroup = useMemo<LookGroupDTOType>(
    () => ({
      id: currentLook.id,
      occasion_name: currentLook.occasion_name,
      sub_occasion_name: currentLook.sub_occasion_name || '',
      base_product_id: currentLook.base_product_id,
      base_product_type: currentLook.base_product_type || '',
      base_product_brand_name: currentLook.base_product_brand_name || '',
      base_product_name: currentLook.base_product_name || '',
      base_product_color: currentLook.base_product_color || '',
      is_pinned: false,
      display_order: currentLook.group_display_order ?? 0,
      primary_look_id: currentLook.id,
      primary_look: toGroupLookSummary(currentLook),
      looks: [toGroupLookSummary(currentLook)],
      qc_summary: null,
      created_on: currentLook.created_on,
      updated_on: currentLook.updated_on,
    }),
    [currentLook],
  );
  const workflowLine = useMemo(() => formatWorkflowLine(workflowGroup), [workflowGroup]);

  const isImageGenerating =
    currentLook.image_generation_status === 'generating' ||
    currentLook.image_generation_status === 'pending';

  useEffect(() => {
    if (!isPollingLook) {
      return;
    }

    const pollLookWithBackoff = (attempt: number): Effect.Effect<void, never, never> => {
      const delayMs = getBackoffDelayMs(attempt);

      return Effect.tryPromise({
        try: () => triggerGetLookById(run.id, true).unwrap(),
        catch: error => error,
      }).pipe(
        Effect.matchEffect({
          onFailure: error => {
            console.error('Error polling look status:', error);
            return Effect.sleep(delayMs).pipe(Effect.zipRight(pollLookWithBackoff(attempt + 1)));
          },
          onSuccess: nextLook =>
            Effect.sync(() => {
              setLatestLookData(nextLook);
              return nextLook.image_generation_status;
            }).pipe(
              Effect.flatMap(status => {
                if (status === 'completed') {
                  return Effect.sync(() => {
                    setIsPollingLook(false);
                    message.success('Image generation completed');
                  });
                }

                if (status === 'failed') {
                  return Effect.sync(() => {
                    setIsPollingLook(false);
                    message.error('Image generation failed');
                  });
                }

                return Effect.sleep(delayMs).pipe(
                  Effect.zipRight(pollLookWithBackoff(attempt + 1)),
                );
              }),
            ),
        }),
      );
    };

    const pollingFiber: RuntimeFiber<void, never> = Effect.runFork(pollLookWithBackoff(0));

    return () => {
      pollingFiber.unsafeInterruptAsFork(FiberId.none);
    };
  }, [isPollingLook, run.id, triggerGetLookById]);

  const handleReject = async () => {
    try {
      await rejectLook({
        look_id: run.id,
        feedback: 'Rejected via admin panel',
        rejection_reason: 'other',
      }).unwrap();

      setLatestLookData({
        ...currentLook,
        qc_status: 'rejected',
      });
      message.success('Look rejected successfully');
    } catch (error) {
      console.error('Error rejecting look:', error);
      message.error('Failed to reject look');
    }
  };

  const handlePublish = async () => {
    try {
      await publishLook({ id: run.id }).unwrap();
      setLatestLookData({
        ...currentLook,
        qc_status: 'published',
      });
      message.success('Look published successfully');
    } catch (error) {
      console.error('Error publishing look:', error);
      message.error('Failed to publish look');
    }
  };

  const handleUnpublish = async () => {
    try {
      await unpublishLook({ id: run.id }).unwrap();
      setLatestLookData({
        ...currentLook,
        qc_status: 'pending',
      });
      message.success('Look unpublished successfully');
    } catch (error) {
      console.error('Error unpublishing look:', error);
      message.error('Failed to unpublish look');
    }
  };

  const handleReRun = async () => {
    try {
      await reRunLook({ id: run.id }).unwrap();
      message.success('Look re-run initiated');
      setLatestLookData({
        ...currentLook,
        image_generation_status: 'pending',
      });
      setIsPollingLook(true);
    } catch (error) {
      console.error('Error re-running look:', error);
      message.error('Failed to re-run look');
    }
  };

  return (
    <div ref={lastItemRef} className="snap-start flex flex-col gap-2 content-visibility-auto p-3">
      <WorkflowRunItemHeader
        currentLook={currentLook}
        isImageGenerating={isImageGenerating}
        isPollingLook={isPollingLook}
        canApprove={canApprove}
        canReject={canReject}
        isReRunning={isReRunning}
        isPublishing={isPublishing}
        isUnpublishing={isUnpublishing}
        isRejecting={isRejecting}
        onReRun={handleReRun}
        onPublish={handlePublish}
        onUnpublish={handleUnpublish}
        onReject={handleReject}
      />

      <LookGroupWorkspaceRow
        group={workflowGroup}
        look={workflowGroup.looks[0]}
        workflowLine={workflowLine}
        onLookUpdated={setLatestLookData}
        publishControl={null}
        eagerLoad={canEdit && !isImageGenerating}
        mediaStickyTopClassName="top-3"
      />
    </div>
  );
};

export default WorkflowRunItem;
